import { useState } from "react";
import { useApprovalActions, useEligibleApprovers, ApprovalRequestType } from "@/hooks/useApprovalSystem";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  assigneeId: string;
  requestId: string;
  requestType: ApprovalRequestType;
}

export const ApprovalForwardModal = ({ open, onClose, assigneeId, requestId, requestType }: Props) => {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const { forward } = useApprovalActions();
  const { data: users } = useEligibleApprovers(requestType);

  const eligible = (users ?? []).filter((u) => u.canApprove && !u.isSelf);

  const handleForward = async () => {
    if (!selectedUser) {
      toast.error("Select an approver to forward to");
      return;
    }
    try {
      await forward.mutateAsync({
        assigneeId,
        requestId,
        forwardToUserId: selectedUser,
        note: note.trim() || undefined,
      });
      toast.success("Request forwarded", { duration: 5000 });
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Forward to Another Approver</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-60 overflow-auto">
          {eligible.map((u) => (
            <label key={u.userId} className="flex items-center gap-2 p-2 rounded hover:bg-accent cursor-pointer">
              <Checkbox
                checked={selectedUser === u.userId}
                onCheckedChange={() => setSelectedUser(u.userId)}
              />
              <span className="text-sm font-medium">{u.displayName}</span>
              <Badge variant="outline" className="text-[10px] ml-auto">{u.roleName}</Badge>
            </label>
          ))}
          {eligible.length === 0 && (
            <p className="text-sm text-muted-foreground">No eligible approvers found.</p>
          )}
        </div>
        <Textarea
          placeholder="Note to forwarded approver"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleForward} disabled={!selectedUser || forward.isPending}>
            <ArrowRight className="h-4 w-4 mr-1" /> Forward Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
